/**
 * NegotiationPage — /contracts/studies/:studyId/negotiations/:negotiationId
 * (WP-103, decision (290), 2026-09-29). Replaces the negotiation modal: a page
 * with a breadcrumb (Studies > protocol > negotiation), the modal's title as
 * its header, and NegotiationDetail as the body.
 *
 * The server has no "get one negotiation" call, so the page loads the study
 * and the study's negotiation list and picks the negotiation from it: an id
 * that is not under this study is "not found" ([OWNER] 291). A 400 or 404 from
 * the study (unknown id, or another site's study) is not found; any other
 * failure is a load error. Every state links back to Studies.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Scale, SearchX } from 'lucide-react';
import { PageHeader, EmptyState, secondaryBtnCls } from '../../../ui';
import { useContracts } from '../../ContractsProvider';
import { getStudy, listNegotiations } from '../../api/client';
import type { Negotiation, Study } from '../../api/client';
import { StudiesBreadcrumb } from './StudiesBreadcrumb';
import { NegotiationDetail, KIND_LABELS, negotiationCrumbLabel } from './NegotiationDetail';

type LoadStatus = 'loading' | 'ready' | 'studyNotFound' | 'notFound' | 'error';

const STUDIES_CRUMB = { label: 'Studies', to: '/contracts/studies' };

export function NegotiationPage() {
  const { studyId, negotiationId } = useParams();
  const { siteId } = useContracts();
  const [study, setStudy] = useState<Study | null>(null);
  const [negotiation, setNegotiation] = useState<Negotiation | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');

  useEffect(() => {
    if (!siteId || !studyId) return;
    let cancelled = false;
    setStatus('loading');
    setStudy(null);
    setNegotiation(null);
    Promise.all([getStudy(studyId), listNegotiations(studyId)])
      .then(([loadedStudy, negotiations]) => {
        if (cancelled) return;
        if (!loadedStudy) {
          setStatus('studyNotFound');
          return;
        }
        setStudy(loadedStudy);
        const found = (Array.isArray(negotiations) ? negotiations : []).find((n) => n.id === negotiationId);
        if (!found) {
          setStatus('notFound');
          return;
        }
        setNegotiation(found);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const httpStatus = (err as { response?: { status?: number } })?.response?.status;
        setStatus(httpStatus === 400 || httpStatus === 404 ? 'studyNotFound' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, studyId, negotiationId]);

  const backToStudies = (
    <Link data-testid="studies-not-found-back" to="/contracts/studies" className={secondaryBtnCls}>
      Back to Studies
    </Link>
  );

  if (status === 'ready' && study && negotiation) {
    return (
      <div data-testid="negotiation-page" className="space-y-4">
        <StudiesBreadcrumb
          items={[STUDIES_CRUMB, { label: study.protocol_number, to: `/contracts/studies/${study.id}` }, { label: negotiationCrumbLabel(negotiation) }]}
        />
        <PageHeader title={`${KIND_LABELS[negotiation.kind]} — ${study.protocol_number}`} icon={Scale} />
        <NegotiationDetail negotiation={negotiation} study={study} onNegotiationUpdated={setNegotiation} onStudyUpdated={(updated) => setStudy((prev) => ({ ...updated, tags: updated.tags ?? prev?.tags ?? study.tags }))} />
      </div>
    );
  }

  if (status === 'notFound' && study) {
    return (
      <div data-testid="negotiation-page" className="space-y-4">
        <StudiesBreadcrumb items={[STUDIES_CRUMB, { label: study.protocol_number, to: `/contracts/studies/${study.id}` }, { label: 'Not found' }]} />
        <div data-testid="negotiation-not-found">
          <EmptyState
            icon={SearchX}
            title="Negotiation not found"
            hint="It may have been removed, or it belongs to another study."
            variant="card"
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Link data-testid="negotiation-not-found-study" to={`/contracts/studies/${study.id}`} className={secondaryBtnCls}>
                  {`Back to ${study.protocol_number}`}
                </Link>
                {backToStudies}
              </div>
            }
          />
        </div>
      </div>
    );
  }

  if (status === 'studyNotFound') {
    return (
      <div data-testid="negotiation-page" className="space-y-4">
        <StudiesBreadcrumb items={[STUDIES_CRUMB, { label: 'Not found' }]} />
        <div data-testid="study-not-found">
          <EmptyState
            icon={SearchX}
            title="Study not found"
            hint="It may have been removed, or it belongs to another site."
            variant="card"
            action={backToStudies}
          />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div data-testid="negotiation-page" className="space-y-4">
        <StudiesBreadcrumb items={[STUDIES_CRUMB, { label: 'Error' }]} />
        <div data-testid="negotiation-load-error">
          <EmptyState icon={SearchX} title="Could not load this negotiation." variant="card" action={backToStudies} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="negotiation-page" className="space-y-4">
      <StudiesBreadcrumb items={[STUDIES_CRUMB, { label: 'Loading…' }]} />
      <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
    </div>
  );
}

export default NegotiationPage;
